import { ReactElement } from 'react';

import { HistoricalVisitSimpleQuery } from '@dreryk/egabinet-visit-frontend-api';
import {
   ConfidentialResourceAccessRequestCreateCommand,
   ConfidentialResourceAccessSelfGrantCommand,
} from '@dreryk/egabinet-medical-data-confidentiality-frontend-api';

import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';
import { PromiseVoidFunction, Translate } from '@dreryk/react-common';
import {
   ButtonVariant,
   dispatchSnackbarPositiveEvent,
   IconFillColor,
   IconSize,
   IconWrapper,
   ShieldProtectedIcon,
   StyledFlex,
} from '@dreryk/react-components';

import { useCurrentPractitionerGlobalState } from '~libs.shared.account';

import { historicalVisitsTranslationKeys } from '../../translations';

import { StyledConfidentialAccessButton } from './HistoricalVisitConfidentialAccessButton.styled';

type HistoricalVisitConfidentialAccessButtonProps = {
   readonly visitId: number;
};

export const HistoricalVisitConfidentialAccessButton = (
   props: HistoricalVisitConfidentialAccessButtonProps
): ReactElement | null => {
   const { getCurrentPractitioner } = useCurrentPractitionerGlobalState();
   const confidentialAccessLevel = getCurrentPractitioner()?.confidentialAccessLevel;

   const { executeAsync: requestAccess } = useCqrsCommand<void, void>(
      () => {
         const confidentialAccessLevel = getCurrentPractitioner()?.confidentialAccessLevel;
         const Command =
            confidentialAccessLevel === 'MONITORED'
               ? ConfidentialResourceAccessSelfGrantCommand
               : ConfidentialResourceAccessRequestCreateCommand;
         const command = new Command();
         command.resourceId = props.visitId;
         command.resourceType = 'VISIT';
         return command;
      },
      {
         onSuccess: () => {
            const confidentialAccessLevel = getCurrentPractitioner()?.confidentialAccessLevel;
            const snackbarMessage =
               confidentialAccessLevel === 'MONITORED'
                  ? historicalVisitsTranslationKeys.confidentialAccess.selfGrantSuccess
                  : historicalVisitsTranslationKeys.confidentialAccess.requestSuccess;

            dispatchSnackbarPositiveEvent(snackbarMessage);
            refetchCqrsQuery(HistoricalVisitSimpleQuery);
         },
      }
   );

   const handleClick: PromiseVoidFunction = async () => {
      await requestAccess();
   };

   if (!confidentialAccessLevel) {
      return null;
   }

   return (
      <StyledConfidentialAccessButton
         testId={'confidentialAccessRequest'}
         variant={ButtonVariant.SECONDARY}
         onClick={handleClick}
         compact
         label={
            <StyledFlex gap={'4px'} alignItems={'center'}>
               <IconWrapper size={IconSize.SMALL} fillColor={IconFillColor.PRIMARY} testId={'confidentialAccessIcon'}>
                  <ShieldProtectedIcon />
               </IconWrapper>
               <Translate>{historicalVisitsTranslationKeys.confidentialAccess.request}</Translate>
            </StyledFlex>
         }
      />
   );
};
